import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { apiError } from '../../../core/util/forms';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  LucideAngularModule,
  AlertCircle,
  ArrowRight,
  ChevronDown,
  Eye,
  EyeOff,
  Info,
  Lock,
  Mail
} from 'lucide-angular';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { LanguageSwitcherComponent } from '../../../shared/language-switcher/language-switcher.component';

/**
 * The sign-in screen: one card in the middle of a softly tinted page.
 *
 * Everything on it reads the theme (`theme.css`), so the card, the glow
 * behind it and the button all follow whichever colour theme was chosen on
 * this device. The language switcher sits above the form, because somebody
 * who reads only Kannada has to be able to switch before they can read the
 * field labels.
 */

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, TranslocoDirective, LanguageSwitcherComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly Eye = Eye;
  readonly EyeOff = EyeOff;
  readonly Lock = Lock;
  readonly Mail = Mail;
  readonly Info = Info;
  readonly AlertCircle = AlertCircle;
  readonly ArrowRight = ArrowRight;
  readonly ChevronDown = ChevronDown;

  username = '';
  password = '';
  showPassword = false;
  isLoading = false;
  errorMessage: string | null = null;
  /** Why the previous session ended, when this wasn't a click on "Sign out" —
   *  e.g. a changed academic year invalidates the token everyone is holding. */
  infoMessage: string | null = null;

  /**
   * One seeded account per role, so the demo can be driven without reading the
   * seed script. They come from the environment and are empty in a deployed
   * build, so a real school's sign-in page never carries a shared password —
   * and the production bundle does not contain one.
   */
  readonly demoAccounts = environment.demoAccounts;
  readonly demoPassword = environment.demoPassword;
  readonly showDemo = this.demoAccounts.length > 0;
  /** The demo list starts folded: it is a convenience, not the page. */
  demoOpen = false;

  /** Where "Contact support" goes; without one the footer points at the school office instead. */
  readonly supportEmail = environment.supportEmail;
  readonly year = new Date().getFullYear();

  ngOnInit(): void {
    this.infoMessage = this.route.snapshot.queryParamMap.get('reason');
  }

  /** A field only turns red once a submit has actually failed on it. */
  get usernameInvalid(): boolean {
    return !!this.errorMessage && !this.username.trim();
  }

  get passwordInvalid(): boolean {
    return !!this.errorMessage && !this.password;
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleDemo(): void {
    this.demoOpen = !this.demoOpen;
  }

  /** One click: fill a seeded account and sign straight in. */
  signInAsDemo(email: string): void {
    if (this.isLoading) {
      return;
    }
    this.username = email;
    this.password = this.demoPassword;
    this.signIn();
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    if (!this.username.trim() || !this.password) {
      this.errorMessage = this.transloco.translate('login.bothRequired');
      return;
    }
    this.signIn();
  }

  private signIn(): void {
    this.errorMessage = null;
    this.isLoading = true;

    this.auth.login(this.username.trim(), this.password).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.user.must_change_password) {
          // A password somebody else chose is not yet theirs, and the API
          // refuses everything else until it is replaced.
          this.router.navigateByUrl('/change-password');
          return;
        }
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        this.router.navigateByUrl(this.auth.resolveRedirect(returnUrl, response.user.role));
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        // The server refuses in the language this screen is showing — the
        // interceptor sent `Accept-Language` even on this call, which is the
        // whole reason it is set before the sign-in early return. `apiError`
        // reads the sentence out of the refusal; these are what to say when
        // the server did not get far enough to have an opinion.
        if (error.status === 0) {
          this.errorMessage = this.transloco.translate('errors.offline');
        } else if (error.status === 401) {
          this.errorMessage = apiError(error, this.transloco.translate('login.wrongCredentials'));
        } else if (error.status === 403) {
          this.errorMessage = apiError(error, this.transloco.translate('login.cannotSignIn'));
        } else {
          this.errorMessage = this.transloco.translate('login.unknownProblem');
        }
      }
    });
  }
}
